import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from './SafeIcon'

const { FiChevronDown, FiGlobe, FiCheck } = FiIcons

function LanguageSelector() {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const languages = [
    { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', region: 'DE' },
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', region: 'EU' },
    { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', region: 'FR' }
  ]

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0]

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode)
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-1.5 px-2 py-1 rounded-md transition-all duration-200 border border-transparent ${
          isOpen ? 'bg-gray-100 border-gray-200 text-[#FF6600]' : 'text-gray-600 hover:text-[#FF6600] hover:bg-gray-50'
        }`}
      >
        <SafeIcon icon={FiGlobe} className="w-3.5 h-3.5" />
        <span className="font-medium text-xs">{currentLanguage.nativeName}</span>
        <span className="text-[10px] bg-gray-100 text-gray-500 px-1 rounded border border-gray-200 hidden sm:inline-block">
          {currentLanguage.region}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <SafeIcon icon={FiChevronDown} className="w-3 h-3" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-[100] overflow-hidden origin-top-right"
          >
            <div className="py-1">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Select Language
                </span>
              </div>
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between group transition-colors ${
                    language.code === i18n.language 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-700 hover:bg-gray-50 hover:text-[#FF6600]'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-lg leading-none filter drop-shadow-sm">{language.flag}</span>
                    <div className="flex flex-col leading-none">
                      <span className="font-medium">{language.nativeName}</span>
                      <span className="text-[10px] text-gray-400 mt-0.5 group-hover:text-orange-400">
                        {language.name}
                      </span>
                    </div>
                  </div>
                  {language.code === i18n.language && (
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-blue-500" />
                  )}
                </button>
              ))}
            </div>
            
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-500 text-center">
              Auto-Translation by 1688Euro
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default LanguageSelector